import { PencilIcon } from '@heroicons/react/24/outline';
import React from 'react';

import {
  DiscriminatedMatterUpdatedEvent,
  MatterSchema,
} from '@/services/api/generated/matterChangesService';

import {
  ActivityBody,
  ActivityIcon,
  ActivityItem,
  FieldsChanged,
} from '../activity-feed-v2';
import { ActivityDescription } from '../activity-feed-v2/ActivityDescription';
import { getUserFromEvent } from '../activity-feed-v2/ActivityDiffList';
import { CollapsibleList } from '../activity-feed-v2/CollapsibleList';
import { formatFieldName } from '../activity-feed-v2/utils';

export interface MatterUpdatedEventV2Props {
  event: DiscriminatedMatterUpdatedEvent;
}

/**
 * V2 renderer for Matter Updated events that uses the `previous` field
 * to show only what changed, instead of parsing the diff format.
 */
export const MatterUpdatedEventV2: React.FC<MatterUpdatedEventV2Props> = ({
  event,
}) => {
  const { matter, previous } = event.payload;
  const user = getUserFromEvent(event);

  const icon = (
    <ActivityIcon
      icon={
        <div className="flex h-6 w-6 items-center justify-center rounded-full bg-indigo-100">
          <PencilIcon className="h-4 w-4 text-indigo-600" />
        </div>
      }
    />
  );

  // Custom field renderer for matter-specific fields
  const renderField = (
    name: keyof MatterSchema,
    value: MatterSchema[keyof MatterSchema],
  ) => {
    const label = formatFieldName(String(name));

    // Clients array - show as collapsible list
    if (name === 'clients' && Array.isArray(value)) {
      // Type guard to ensure we have MatterClientSchema objects
      const clients = value.filter(
        (
          c,
        ): c is import('@/services/api/generated/matterChangesService').MatterClientSchema =>
          typeof c === 'object' && c !== null && 'name' in c,
      );
      if (clients.length === 0) return null;

      if (clients.length <= 2) {
        return (
          <ActivityDescription>
            <span className="font-medium">{label}:</span>{' '}
            {clients
              .map((c) => c.name)
              .filter(Boolean)
              .join(', ')}
          </ActivityDescription>
        );
      }

      return (
        <div>
          <ActivityDescription>
            <span className="font-medium">{label}:</span>
          </ActivityDescription>
          <CollapsibleList
            items={clients.map((client, idx) => {
              const details: string[] = [];
              if (client.name) details.push(`Name: ${client.name}`);
              if (client.email) details.push(`Email: ${client.email}`);
              if (client.phone_numbers && client.phone_numbers.length > 0) {
                client.phone_numbers.forEach((phone) => {
                  details.push(`Phone: ${phone.phone_number}`);
                });
              }
              return (
                <div key={idx} className="space-y-0.5">
                  {details.map((d, i) => (
                    <div key={i}>{d}</div>
                  ))}
                </div>
              );
            })}
            showLabel={`Show Clients (${clients.length})`}
            hideLabel="Hide Clients"
            defaultExpanded={clients.length <= 2}
          />
        </div>
      );
    }

    // Services array - show as comma-separated or collapsible
    if (name === 'services' && Array.isArray(value)) {
      const services = value as string[];
      if (services.length === 0) return null;

      if (services.length <= 3) {
        return (
          <ActivityDescription>
            <span className="font-medium">{label}:</span> {services.join(', ')}
          </ActivityDescription>
        );
      }

      return (
        <div>
          <ActivityDescription>
            <span className="font-medium">{label}:</span>
          </ActivityDescription>
          <CollapsibleList
            items={services.map((service, idx) => (
              <span key={idx}>{service}</span>
            ))}
            showLabel={`Show Services (${services.length})`}
            hideLabel="Hide Services"
          />
        </div>
      );
    }

    // Front attachments - show count
    if (
      name === 'front_attachments' &&
      Array.isArray(value) &&
      value.length > 0
    ) {
      return (
        <ActivityDescription>
          <span className="font-medium">{label}:</span> {value.length}{' '}
          attachment(s)
        </ActivityDescription>
      );
    }

    // Date fields - format nicely
    if (
      (name === 'date_of_loss' || name === 'statute_of_limitations_date') &&
      value &&
      typeof value === 'string'
    ) {
      return (
        <ActivityDescription>
          <span className="font-medium">{label}:</span>{' '}
          {new Date(value).toLocaleDateString()}
        </ActivityDescription>
      );
    }

    // Boolean fields
    if (name === 'contact_client_directly' && typeof value === 'boolean') {
      return (
        <ActivityDescription>
          <span className="font-medium">{label}:</span> {value ? 'Yes' : 'No'}
        </ActivityDescription>
      );
    }

    // Default rendering for simple fields
    return (
      <ActivityDescription>
        <span className="font-medium">{label}:</span> {String(value)}
      </ActivityDescription>
    );
  };

  const body = (
    <ActivityBody>
      <FieldsChanged<MatterSchema>
        previous={previous}
        current={matter}
        fieldsToCheck={[
          'name',
          'type',
          'matter_type',
          'status',
          'clients',
          'services',
          'date_of_loss',
          'statute_of_limitations_date',
          'city',
          'state',
          'contact_client_directly',
          'front_attachments',
          'external_cms_url',
          'referral_source_description',
        ]}
        renderField={renderField}
      />
    </ActivityBody>
  );

  return (
    <ActivityItem
      icon={icon}
      title="Matter Updated"
      timestamp={new Date(event.occurred_at ?? new Date())}
      user={user}
      body={body}
    />
  );
};
